/**
 * Server I/O for viewer-only XYZ transform overrides.
 *
 * Backed by `/api/viewer/overrides` (see api-server.js). Stored per-user
 * × per-folder in MongoDB so a user gets the same gizmo tweaks back on
 * any browser, while the app/admin model positions stay independent.
 *
 * Auth: requires the JWT in sessionStorage / localStorage (same shape
 * the rest of the viewer uses). Unauthenticated viewers (e.g. someone
 * opening a public share link) silently no-op every call — the share
 * payload already carries its own snapshot of overrides.
 */

export interface ViewerOverrideVec {
  x: number;
  y: number;
  z: number;
}

export interface ViewerOverride {
  position: ViewerOverrideVec;
  rotation: ViewerOverrideVec;
  scale: ViewerOverrideVec;
}

export type ViewerOverridesMap = Record<string, ViewerOverride>;

function getToken(): string {
  return (
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    ""
  );
}

function authHeaders(): HeadersInit {
  const token = getToken();
  const base: Record<string, string> = { "Content-Type": "application/json" };
  if (token) base.Authorization = `Bearer ${token}`;
  return base;
}

/** True iff we have a JWT to talk to the API server. */
export function canUseServerOverrides(): boolean {
  return !!getToken();
}

/**
 * Fetch the saved overrides for `(currentUser, folder)`. Returns an empty
 * map on miss / unauthenticated / network error — callers can then fall
 * back to localStorage.
 */
export async function loadOverridesFromServer(
  authBase: string,
  folder: string
): Promise<ViewerOverridesMap> {
  if (!canUseServerOverrides()) return {};
  try {
    const url = `${authBase}/api/viewer/overrides?folder=${encodeURIComponent(folder || "")}`;
    const r = await fetch(url, { headers: authHeaders() });
    if (!r.ok) {
      console.warn(`[viewerOverridesApi] GET ${url} → HTTP ${r.status}`);
      return {};
    }
    const data = await r.json();
    if (!data?.success || !data?.overrides || typeof data.overrides !== "object") {
      return {};
    }
    return data.overrides as ViewerOverridesMap;
  } catch (err) {
    console.warn("[viewerOverridesApi] load failed", err);
    return {};
  }
}

/**
 * Replace the entire saved map for `(currentUser, folder)`. Returns true
 * on success (HTTP 2xx + success:true). Designed to be debounced from
 * the caller — the body is just a few small numbers per model so size
 * isn't a concern but request rate is.
 */
export async function saveOverridesToServer(
  authBase: string,
  folder: string,
  overrides: ViewerOverridesMap
): Promise<boolean> {
  if (!canUseServerOverrides()) return false;
  try {
    const r = await fetch(`${authBase}/api/viewer/overrides`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ folder: folder || "", overrides }),
    });
    if (!r.ok) {
      console.warn(`[viewerOverridesApi] PUT → HTTP ${r.status}`);
      return false;
    }
    const data = await r.json().catch(() => null);
    return !!data?.success;
  } catch (err) {
    console.warn("[viewerOverridesApi] save failed", err);
    return false;
  }
}

/**
 * Delete a single model's override (or, when modelId is omitted, wipe
 * the whole folder's overrides). Returns true on success.
 */
export async function deleteOverridesOnServer(
  authBase: string,
  folder: string,
  modelId?: string
): Promise<boolean> {
  if (!canUseServerOverrides()) return false;
  try {
    const params = new URLSearchParams();
    params.set("folder", folder || "");
    if (modelId) params.set("modelId", modelId);
    const r = await fetch(
      `${authBase}/api/viewer/overrides?${params.toString()}`,
      { method: "DELETE", headers: authHeaders() }
    );
    if (!r.ok) {
      console.warn(`[viewerOverridesApi] DELETE → HTTP ${r.status}`);
      return false;
    }
    const data = await r.json().catch(() => null);
    return !!data?.success;
  } catch (err) {
    console.warn("[viewerOverridesApi] delete failed", err);
    return false;
  }
}

/**
 * Tiny debouncer used by the viewer to batch rapid `objectChange`
 * commits — typing on the gizmo can fire many in a row when the user
 * adjusts multiple axes before letting go.
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  ms: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, ms);
  };
}
